import { Alert, Button, Group, Paper, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useFirestore, useFunctions } from '../helpers/firebaseContext';
import { Customer, KfhtradeBatchRequest, KfhtradeBatchResult } from '../types';
import { CustomerSelectComponent } from './customer_select_component';

type SkippedCustomer = { id: string; clientName: string; crmID: string; customerNo: string };

export default function KfhtradeBatchSkips({ opened, busy, onBusyChange, onSkipped }: {
    opened: boolean; busy: boolean; onBusyChange: (busy: boolean) => void; onSkipped: () => void;
}) {
    const db = useFirestore();
    const functions = useFunctions();
    const [selected, setSelected] = useState<Customer>();
    const [skipped, setSkipped] = useState<SkippedCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [listError, setListError] = useState('');
    const [searchKey, setSearchKey] = useState(0);

    useEffect(() => {
        if (!opened) return;
        setLoading(true); setListError('');
        return onSnapshot(query(collection(db, 'kfhtradeUpdates'), where('manualSkip', '==', true)), snapshot => {
            setSkipped(snapshot.docs.map(doc => ({
                id: doc.id, clientName: doc.get('clientName') || doc.id,
                crmID: doc.get('crmID') || '', customerNo: doc.get('customerNo') || '',
            })).sort((a, b) => a.clientName.localeCompare(b.clientName)));
            setLoading(false);
        }, err => { setLoading(false); setListError(`Could not load skipped customers: ${err.message}`); });
    }, [db, opened]);

    const addSkip = async () => {
        if (!selected?.id) return;
        setSaving(true); onBusyChange(true); setError('');
        try {
            await httpsCallable<KfhtradeBatchRequest, KfhtradeBatchResult>(functions, 'kfhtradeBatchUpdates')({
                action: 'skip', customerIDs: [selected.id],
            });
            onSkipped(); setSelected(undefined); setSearchKey(value => value + 1);
        } catch (err: any) { setError(err?.message || 'Could not skip customer'); }
        finally { setSaving(false); onBusyChange(false); }
    };
    const alreadySkipped = !!selected?.id && skipped.some(customer => customer.id === selected.id);

    return <Paper withBorder p="sm">
        <Stack gap="xs">
            <Text fw={600}>Skip customers</Text>
            <Text size="sm">Search for a customer and add them to the skip list. Saved customers are excluded from all future DFN KYC renewal batches.</Text>
            <CustomerSelectComponent key={searchKey} submitType="object" textProps={{
                label: 'Find a customer to skip', placeholder: 'Search by name, Civil ID, mobile, or CRM ID',
                required: false, disabled: busy,
            }} handleSubmit={customer => {
                setSelected(typeof customer === 'object' ? customer : undefined); setError('');
            }} />
            {selected?.id && <Text size="sm">Selected: {selected.name_en || selected.name_ar || selected.id} · CRM ID: {selected.crmID || selected.id} · KFHTrade ID: {selected.kfhtrade_id || 'Not available'}</Text>}
            {error && <Alert color="red">{error}</Alert>}
            <Group justify="flex-end">
                <Button variant="light" color="orange" loading={saving} disabled={busy || !selected?.id || alreadySkipped} onClick={addSkip}>
                    {alreadySkipped ? 'Already in skip list' : 'Add to skip list'}
                </Button>
            </Group>
            {listError && <Alert color="red">{listError}</Alert>}
            <Text size="sm" fw={500}>{loading ? 'Loading skip list…' : `${skipped.length} manually skipped customer${skipped.length === 1 ? '' : 's'}`}</Text>
            {!!skipped.length && <ScrollArea.Autosize mah={200} type="auto" offsetScrollbars>
                <Table miw={420} striped>
                    <Table.Thead><Table.Tr><Table.Th>Client</Table.Th><Table.Th>CRM ID</Table.Th><Table.Th>KFHTrade ID</Table.Th></Table.Tr></Table.Thead>
                    <Table.Tbody>{skipped.map(customer => <Table.Tr key={customer.id}>
                        <Table.Td>{customer.clientName}</Table.Td><Table.Td>{customer.crmID || customer.id}</Table.Td><Table.Td>{customer.customerNo || '—'}</Table.Td>
                    </Table.Tr>)}</Table.Tbody>
                </Table>
            </ScrollArea.Autosize>}
        </Stack>
    </Paper>;
}
